import React, { useState } from 'react';

const cardStyle = {
  background: 'var(--surface)',
  border: '0.5px solid var(--border)',
  borderTop: '2px solid var(--accent)',
  borderRadius: 'var(--radius)',
  padding: '1rem 1.25rem',
  fontFamily: 'Georgia, serif',
  fontSize: '0.95rem',
  lineHeight: '1.65',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  color: '#222',
};

export default function FindDuplicates() {
  const [groups, setGroups] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [done, setDone] = useState(false);
  const [mergeState, setMergeState] = useState(null); // { keepCardId, text }
  const [saving, setSaving] = useState(false);

  const runAnalysis = async () => {
    setLoading(true);
    setHasRun(true);
    setDone(false);
    setMergeState(null);
    try {
      const res = await fetch('/api/cards/duplicates', { credentials: 'include' });
      const data = await res.json();
      const grps = Array.isArray(data) ? data : [];
      setGroups(grps);
      setCurrentIdx(0);
      if (grps.length === 0) setDone(true);
    } catch {
      setGroups([]);
      setDone(true);
    }
    setLoading(false);
  };

  const advance = () => {
    setMergeState(null);
    if (currentIdx + 1 >= groups.length) {
      setDone(true);
    } else {
      setCurrentIdx(i => i + 1);
    }
  };

  const handleDelete = async (cardId) => {
    await fetch(`/api/cards/${cardId}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'deleted' })
    });
    advance();
  };

  const handleStartMerge = (card) => {
    setMergeState({ keepCardId: card.id, text: card.body });
  };

  const handleSaveMerge = async () => {
    const group = groups[currentIdx];
    const { keepCardId, text } = mergeState;
    if (!text.trim()) return;
    setSaving(true);
    await fetch(`/api/cards/${keepCardId}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body: text.trim() })
    });
    for (const c of group) {
      if (c.id !== keepCardId) {
        await fetch(`/api/cards/${c.id}`, {
          method: 'PATCH', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'deleted' })
        });
      }
    }
    setSaving(false);
    advance();
  };

  // INITIAL
  if (!hasRun) {
    return (
      <div style={{ maxWidth: 560 }}>
        <div className="page-header">
          <h1>Find Duplicates</h1>
          <div className="subtitle">Find cards with similar or overlapping content.</div>
        </div>
        <button className="btn btn-primary" onClick={runAnalysis} style={{ fontSize: '1rem', padding: '0.75rem 2rem' }}>
          Analyze cards
        </button>
      </div>
    );
  }

  // LOADING
  if (loading) {
    return (
      <div className="inspire-container">
        <div className="spinner" />
      </div>
    );
  }

  // ALL DONE
  if (done) {
    return (
      <div style={{ maxWidth: 560 }}>
        <div className="page-header">
          <h1>Find Duplicates</h1>
        </div>
        <p style={{ fontFamily: 'sans-serif', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
          {groups.length === 0 ? 'No similar cards found.' : 'All groups reviewed.'}
        </p>
        <button className="btn btn-secondary" onClick={runAnalysis}>Run again</button>
      </div>
    );
  }

  const group = groups[currentIdx];
  const othersCount = group.length - 1;

  return (
    <div style={{ maxWidth: 560 }}>
      <div className="page-header">
        <h1>Find Duplicates</h1>
        <div className="subtitle">Group {currentIdx + 1} of {groups.length}</div>
      </div>

      {/* Cards in group */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', marginBottom: '1.25rem' }}>
        {group.map(card => (
          <div key={card.id}>
            <div style={cardStyle}>{card.body}</div>
            {!mergeState && (
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-danger"
                  onClick={() => handleDelete(card.id)}
                  style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}
                >
                  Delete this
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => handleStartMerge(card)}
                  style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}
                >
                  Merge into this
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Merge editor */}
      {mergeState && (
        <div style={{ marginBottom: '1.25rem' }}>
          <label className="form-label">
            Edit the text to keep — {othersCount} other card{othersCount !== 1 ? 's' : ''} will be trashed
          </label>
          <textarea
            className="form-textarea"
            value={mergeState.text}
            onChange={e => setMergeState(s => ({ ...s, text: e.target.value }))}
            rows={6}
            autoFocus
          />
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button
              className="btn btn-primary"
              onClick={handleSaveMerge}
              disabled={saving || !mergeState.text.trim()}
            >
              {saving ? 'Saving…' : `Save & trash ${othersCount} other${othersCount !== 1 ? 's' : ''}`}
            </button>
            <button className="btn btn-secondary" onClick={() => setMergeState(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Keep all */}
      {!mergeState && (
        <div style={{ borderTop: '0.5px solid var(--border)', paddingTop: '1rem' }}>
          <button className="btn btn-secondary" onClick={advance}>
            Keep both
          </button>
        </div>
      )}
    </div>
  );
}
